import type { ShareProfileRoomFragment } from "~/__generated__/graphql"
import { EVENT_IDS, SCREENS, SHARE_PAGES } from "~/common/analytics"
import { brandAppName } from "~/common/brand"
import { getMetaContent } from "~/common/get-meta-content"
import { getDeeplinkUrl } from "~/common/platform"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import { useCollectEmail } from "~/hooks/use-collect-email"
import {
  type EmailSubmissionParams,
  useInviteFriendEmailSubmission,
  useRoomShareEmailSubmission,
} from "~/hooks/use-email-submission"
import {
  type PhoneSubmissionParams,
  useInviteFriendPhoneSubmission,
  useRoomSharePhoneSubmission,
} from "~/hooks/use-phone-submission"
import { useScreenTracking } from "~/hooks/use-screen-tracking"
import { getInviterData, getProfileData, getProfileRooms } from "~/share/share-embedded-data"
import { ProfileCard } from "./components/user-profile/profile-card"
import { ProfileRoomsSection } from "./components/user-profile/profile-rooms-section"
import {
  ShareScreenBase,
  SKIP_WAITLIST_TITLE,
  skipWaitlistSuccessMessage,
} from "./share-screen-base"

export const ShareUserProfileScreen = (): JSX.Element => {
  const profile = getProfileData()
  const inviter = getInviterData() ?? profile
  const rooms = getProfileRooms()
  const defaultRoom: ShareProfileRoomFragment | undefined = rooms[0]
  const collectEmail = useCollectEmail()
  const roomPhoneSubmission = useRoomSharePhoneSubmission()
  const roomEmailSubmission = useRoomShareEmailSubmission()
  const invitePhoneSubmission = useInviteFriendPhoneSubmission()
  const inviteEmailSubmission = useInviteFriendEmailSubmission()
  const { phoneSubmission, emailSubmission, submitPhone, submitEmail } = defaultRoom
    ? {
        phoneSubmission: roomPhoneSubmission,
        emailSubmission: roomEmailSubmission,
        submitPhone: (params: PhoneSubmissionParams) =>
          roomPhoneSubmission.submitPhoneNumber({ ...params, roomId: defaultRoom.id }),
        submitEmail: (params: EmailSubmissionParams) =>
          roomEmailSubmission.submitEmail({ ...params, roomId: defaultRoom.id }),
      }
    : {
        phoneSubmission: invitePhoneSubmission,
        emailSubmission: inviteEmailSubmission,
        submitPhone: invitePhoneSubmission.submitPhoneNumber,
        submitEmail: inviteEmailSubmission.submitEmail,
      }

  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getDeeplinkUrl(appEnvironment, `profile/${profile.id}`)
  const displayName = profile.displayName || profile.username
  useDocumentTitleDescription(
    `${displayName} (@${profile.username}) on ${brandAppName()}`,
    profile.bio ?? null
  )

  const analyticsProps = { profile_user_id: profile.id, profile_username: profile.username }
  useScreenTracking(SCREENS.Share, {
    share_page: SHARE_PAGES.Profile,
    ...analyticsProps,
    deeplink_url: deeplinkUrl,
  })

  const onJoinPress = async () => {
    const params = {
      successMessage: skipWaitlistSuccessMessage(collectEmail),
      successTitle: SKIP_WAITLIST_TITLE,
      trackingData: {
        unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
        share_page: SHARE_PAGES.Profile,
        inviter_id: inviter.id,
        inviter_username: inviter.username,
        room_id: defaultRoom?.id,
        room_handle: defaultRoom?.roomHandle ?? undefined,
        deeplink_url: deeplinkUrl,
      },
    }

    await (collectEmail ? submitEmail(params) : submitPhone(params))
  }

  const inputProps = collectEmail
    ? {
        inputMode: "email" as const,
        emailValue: emailSubmission.emailValue,
        setEmailValue: emailSubmission.setEmailValue,
      }
    : {
        inputMode: "phone" as const,
        phoneValue: phoneSubmission.phoneValue,
        setPhoneValue: phoneSubmission.setPhoneValue,
      }

  return (
    <ShareScreenBase
      deeplinkUrl={deeplinkUrl}
      onJoinPress={onJoinPress}
      analyticsProps={analyticsProps}
      hideTitle
      {...inputProps}
      renderContent={() => (
        <div className="flex w-full max-w-[480px] flex-col gap-6 px-4 pb-4">
          <ProfileCard profile={profile} />
          <ProfileRoomsSection
            title="Where I'm Chatting"
            rooms={rooms}
            referrerUsername={inviter.username}
          />
        </div>
      )}
    />
  )
}

export default ShareUserProfileScreen
